import { useEffect, useState } from 'react';
import { dashboardAPI, aiAPI, exchangeRateAPI } from '../api';
import { TrendingUp, TrendingDown, Plus, AlertCircle, CreditCard, DollarSign, Wallet, RefreshCw } from 'lucide-react';
import { AreaChart, Area, ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const COLORS = ['#14532d', '#78716c', '#bef264', '#a3e635', '#84cc16', '#65a30d'];

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [insights, setInsights] = useState(null);
  const [categoryInsights, setCategoryInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [multiCurrency, setMultiCurrency] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState('SAR');
  const [showCurrencyConverter, setShowCurrencyConverter] = useState(false);

  const currencies = ['SAR', 'USD', 'INR', 'EUR', 'GBP', 'AED'];

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const today = new Date();
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

      const [summaryRes, cashFlowRes, categoryRes, multiCurrRes] = await Promise.all([
        dashboardAPI.getSummary(),
        dashboardAPI.getCashFlow(monthStart, monthEnd),
        dashboardAPI.getCategoryInsights(),
        dashboardAPI.getMultiCurrency(currencies.join(',')),
      ]);

      setSummary(summaryRes.data);
      setCashFlow(cashFlowRes.data);
      setCategoryInsights(categoryRes.data);
      setMultiCurrency(multiCurrRes.data);
      setLoading(false);

      try {
        const insightsRes = await aiAPI.getInsights();
        setInsights(insightsRes.data.insights);
      } catch (err) {
        console.log('AI insights not available');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-900 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const expenseData = cashFlow?.expense_by_category
    ? Object.entries(cashFlow.expense_by_category).map(([name, value]) => ({ name, value }))
    : [];

  const netWorthData = [
    { month: 'Jan', value: summary?.net_worth * 0.85 || 0 },
    { month: 'Feb', value: summary?.net_worth * 0.9 || 0 },
    { month: 'Mar', value: summary?.net_worth * 0.92 || 0 },
    { month: 'Apr', value: summary?.net_worth * 0.95 || 0 },
    { month: 'May', value: summary?.net_worth * 0.98 || 0 },
    { month: 'Now', value: summary?.net_worth || 0 },
  ];

  return (
    <div className="min-h-screen bg-stone-50 p-6 md:p-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-5xl font-bold font-heading text-green-900 mb-2">
            Financial Overview
          </h1>
          <p className="text-stone-500 text-lg">Track your wealth, manage your goals</p>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3 mb-8 overflow-x-auto pb-2 scroll-snap-x">
          <Link
            to="/transactions?action=add-expense"
            data-testid="quick-add-expense-btn"
            className="flex-shrink-0 bg-green-900 text-white px-6 py-3 rounded-full font-medium hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-95 scroll-snap-start"
          >
            <Plus className="inline-block w-5 h-5 mr-2" />
            Add Expense
          </Link>
          <Link
            to="/transactions?action=add-income"
            data-testid="quick-add-income-btn"
            className="flex-shrink-0 bg-stone-100 text-stone-900 px-6 py-3 rounded-full font-medium hover:bg-stone-200 transition-all scroll-snap-start"
          >
            <Plus className="inline-block w-5 h-5 mr-2" />
            Add Income
          </Link>
          <Link
            to="/assets"
            data-testid="manage-assets-btn"
            className="flex-shrink-0 bg-stone-100 text-stone-900 px-6 py-3 rounded-full font-medium hover:bg-stone-200 transition-all scroll-snap-start"
          >
            Manage Assets
          </Link>
        </div>

        {/* Alerts */}
        {summary?.alerts && summary.alerts.length > 0 && (
          <div className="mb-8 bg-red-50 border border-red-200 rounded-2xl p-6" data-testid="alerts-section">
            <div className="flex items-start">
              <AlertCircle className="w-6 h-6 text-red-700 mr-3 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-2">Action Required</h3>
                <ul className="space-y-2">
                  {summary.alerts.map((alert, idx) => (
                    <li key={idx} className="text-red-800 text-sm" data-testid={`alert-${idx}`}>
                      {alert.message}
                      {alert.amount && ` - ${alert.amount.toFixed(2)} SAR`}
                      {alert.percentage && ` - ${alert.percentage.toFixed(0)}%`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Main Grid - Bento Layout */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Net Worth Card - Large */}
          <div className="md:col-span-2 md:row-span-2 bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid="net-worth-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-stone-500 uppercase tracking-widest">Net Worth</h2>
              <div className="flex items-center gap-2">
                {summary?.net_worth >= 0 ? (
                  <TrendingUp className="w-5 h-5 text-lime-500" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-red-700" />
                )}
                <button
                  onClick={() => setShowCurrencyConverter(!showCurrencyConverter)}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-all"
                  data-testid="toggle-currency-btn"
                  title="View in other currencies"
                >
                  <RefreshCw className="w-4 h-4 text-stone-500" />
                </button>
              </div>
            </div>
            <div className="mb-6">
              <div className="text-5xl md:text-6xl font-bold font-heading text-green-900 tabular-nums mb-2">
                {summary?.net_worth?.toFixed(2) || '0.00'}
                <span className="text-2xl ml-2 text-stone-500">SAR</span>
              </div>
              <div className="flex gap-4 text-sm mt-4">
                <div>
                  <span className="text-stone-500">Assets: </span>
                  <span className="font-bold text-green-900 tabular-nums">{summary?.total_assets?.toFixed(2)}</span>
                </div>
                <div>
                  <span className="text-stone-500">Liabilities: </span>
                  <span className="font-bold text-red-700 tabular-nums">{summary?.total_liabilities?.toFixed(2)}</span>
                </div>
              </div>
            </div>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={netWorthData}>
                  <defs>
                    <linearGradient id="colorWorth" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14532d" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#14532d" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#14532d"
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorWorth)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Monthly Income */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid="monthly-income-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-stone-500 uppercase tracking-widest">Income</h3>
              <DollarSign className="w-5 h-5 text-green-700" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-bold text-green-900 tabular-nums">
              {summary?.month_income?.toFixed(2) || '0.00'}
            </div>
            <p className="text-sm text-stone-500 mt-1">This month</p>
          </div>

          {/* Monthly Expenses */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid="monthly-expenses-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-stone-500 uppercase tracking-widest">Expenses</h3>
              <CreditCard className="w-5 h-5 text-red-700" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-bold text-red-700 tabular-nums">
              {summary?.month_expenses?.toFixed(2) || '0.00'}
            </div>
            <p className="text-sm text-stone-500 mt-1">This month</p>
          </div>

          {/* Savings */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid="monthly-savings-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-stone-500 uppercase tracking-widest">Savings</h3>
              <Wallet className="w-5 h-5 text-lime-500" strokeWidth={1.5} />
            </div>
            <div className={`text-3xl font-bold tabular-nums ${summary?.month_savings >= 0 ? 'text-green-900' : 'text-red-700'}`}>
              {summary?.month_savings?.toFixed(2) || '0.00'}
            </div>
            <p className="text-sm text-stone-500 mt-1">This month</p>
          </div>

          {/* Savings Rate */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm hover:shadow-md transition-all" data-testid="savings-rate-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-stone-500 uppercase tracking-widest">Savings Rate</h3>
            </div>
            <div className="text-3xl font-bold text-green-900 tabular-nums">
              {summary?.month_income > 0
                ? ((summary.month_savings / summary.month_income) * 100).toFixed(1)
                : '0.0'}
              %
            </div>
            <p className="text-sm text-stone-500 mt-1">Of income</p>
          </div>
        </div>

        {/* Multi-Currency Converter */}
        {showCurrencyConverter && multiCurrency && (
          <div className="mb-8 bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-testid="currency-converter">
            <h3 className="text-lg font-bold font-heading text-green-900 mb-4">Your Net Worth in Different Currencies</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {currencies.map((curr) => {
                const data = multiCurrency.currencies[curr];
                if (!data) return null;
                return (
                  <div
                    key={curr}
                    className="bg-stone-50 rounded-xl p-4 border border-stone-200 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => setSelectedCurrency(curr)}
                    data-testid={`currency-${curr}`}
                  >
                    <div className="text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">
                      {curr}
                    </div>
                    <div className={`text-2xl font-bold tabular-nums ${data.net_worth >= 0 ? 'text-green-900' : 'text-red-700'}`}>
                      {data.net_worth.toFixed(2)}
                    </div>
                    <div className="text-xs text-stone-500 mt-1">
                      1 SAR = {data.exchange_rate.toFixed(4)} {curr}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 text-xs text-stone-500">
              Exchange rates updated: {new Date(multiCurrency.timestamp).toLocaleString()}
            </div>
          </div>
        )}

        {/* Category Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Expense Breakdown Pie */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-testid="expense-breakdown-card">
            <h3 className="text-lg font-bold font-heading text-green-900 mb-6">Expense Breakdown</h3>
            {expenseData.length > 0 ? (
              <div className="flex flex-col md:flex-row items-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={expenseData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {expenseData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        background: '#14532d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '8px 12px',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-4 md:mt-0 md:ml-6 space-y-2 w-full">
                  {expenseData.slice(0, 5).map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div
                          className="w-3 h-3 rounded-full mr-2"
                          style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                        ></div>
                        <span className="text-sm text-stone-600">{item.name}</span>
                      </div>
                      <span className="text-sm font-bold tabular-nums">{item.value.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No expenses this month</p>
            )}
          </div>

          {/* Top Categories */}
          <div className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-testid="top-categories-card">
            <h3 className="text-lg font-bold font-heading text-green-900 mb-6">Top Spending Categories</h3>
            {categoryInsights?.top_5_last_month && categoryInsights.top_5_last_month.length > 0 ? (
              <div className="space-y-4">
                {categoryInsights.top_5_last_month.map((item, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-stone-700">{item.category}</span>
                      <span className="text-sm font-bold tabular-nums">{item.amount.toFixed(2)} SAR</span>
                    </div>
                    <div className="w-full bg-stone-200 rounded-full h-2">
                      <div
                        className="bg-green-900 h-2 rounded-full transition-all"
                        style={{
                          width: `${
                            (item.amount / categoryInsights.top_5_last_month[0].amount) * 100
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-stone-500 text-center py-8">No spending data available</p>
            )}
          </div>
        </div>

        {/* AI Insights */}
        {insights && (
          <div className="bg-gradient-to-r from-green-50 to-lime-50 rounded-2xl p-6 border border-lime-200" data-testid="ai-insights-card">
            <h3 className="text-lg font-bold font-heading text-green-900 mb-4">AI Insights</h3>
            <p className="text-stone-700 leading-relaxed whitespace-pre-line">{insights}</p>
          </div>
        )}
      </div>
    </div>
  );
}
